/**
 * Numbers, spec'd to the reader (design-language#words-numbers-and-anticipation).
 *
 * One module rather than a `toLocaleString` at each call site, because the
 * rules here are editorial decisions rather than formatting preferences, and an
 * editorial decision made twice is made differently.
 *
 * Framework-agnostic and dependency-free, so the portal, the widget and the API
 * can all reach it. It returns STRINGS and never JSX: the tone of a delta is a
 * name (`improving`) rather than a class, so the caller's own design tokens
 * resolve it and this module never learns about Tailwind.
 */

export type Density = 'comfortable' | 'compact';

/**
 * A count, at the density of the surface it sits on.
 *
 * `comfortable` is the launch default everywhere (design-language#words-numbers-and-anticipation) and spells figures out
 * in full: an operator counting DEAD rows needs to know whether it is 1,200 or
 * 12,000, and `1.2k` deletes exactly that digit. `compact` is for a place where
 * the magnitude is the message and the surface is genuinely tight — a sparkline
 * axis, a tile in a dense grid.
 *
 * Density is a CONTRACT, not a fork: the same component reads it off the
 * surface root and both branches are this one function. No component is
 * duplicated for it.
 */
export function formatCount(value: number, density: Density = 'comfortable'): string {
  if (!Number.isFinite(value)) return '—';
  if (density === 'comfortable' || Math.abs(value) < 1000) {
    return new Intl.NumberFormat('en-US').format(value);
  }

  const units: [number, string][] = [
    [1_000_000_000, 'b'],
    [1_000_000, 'm'],
    [1_000, 'k'],
  ];
  const sign = value < 0 ? '-' : '';
  const magnitude = Math.abs(value);
  for (const [size, suffix] of units) {
    if (magnitude >= size) {
      const scaled = magnitude / size;
      // One decimal below 10, none above: `9.4k` carries information, `94.3k`
      // is noise at the width this exists to save.
      const text = scaled < 10 ? scaled.toFixed(1).replace(/\.0$/, '') : Math.round(scaled).toString();
      return `${sign}${text}${suffix}`;
    }
  }
  return `${sign}${magnitude}`;
}

/**
 * Whether a change is good or bad, which is NOT the same as its sign.
 *
 * design-language#words-numbers-and-anticipation is explicit: never raw red/green-by-sign. Rising spend, rising error
 * count and rising queue depth are all positive numbers and all bad news, and a
 * green arrow on any of them actively misleads. So the caller states which
 * direction is good for THIS measure, once, and the tone follows from that.
 */
export type DeltaDirection = 'up-is-good' | 'down-is-good';
export type DeltaTone = 'improving' | 'worsening' | 'flat';

export function deltaTone(change: number, direction: DeltaDirection): DeltaTone {
  if (change === 0 || !Number.isFinite(change)) return 'flat';
  const improving = direction === 'up-is-good' ? change > 0 : change < 0;
  return improving ? 'improving' : 'worsening';
}

/**
 * A signed change, with an explicit `+` on the way up.
 *
 * The `+` is not decoration: without it a table of deltas reads as a table of
 * values, and a reader has to check the column header to know which they are
 * looking at.
 */
export function formatDelta(change: number, density: Density = 'comfortable'): string {
  if (!Number.isFinite(change)) return '—';
  if (change === 0) return '0';
  const sign = change > 0 ? '+' : '-';
  return `${sign}${formatCount(Math.abs(change), density)}`;
}

/**
 * A percentage, with the rounding stated rather than assumed.
 *
 * Rounds to whole percent above 10% and one decimal below it, because the
 * difference between 0.4% and 0.9% of a queue failing is the difference between
 * noise and an incident, while 47% and 47.3% are the same fact.
 */
export function formatPercent(fraction: number): string {
  if (!Number.isFinite(fraction)) return '—';
  const percent = fraction * 100;
  const magnitude = Math.abs(percent);
  if (magnitude === 0) return '0%';
  if (magnitude < 10) return `${Number(percent.toFixed(1))}%`;
  return `${Math.round(percent)}%`;
}

/**
 * Bytes at human scale. Binary units, because this describes what a storage
 * account charges for and what an upload limit rejects on.
 */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes)) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * How long ago, in the coarsest unit that is still true.
 *
 * Coarse on purpose. "3 hours ago" and "3 hours 14 minutes ago" support the
 * same decision, and the second invites a reader to believe the precision is
 * meaningful when the underlying timestamp may be a poll interval old.
 */
export function formatAge(fromIso: string, now: number): string {
  const then = new Date(fromIso).getTime();
  if (Number.isNaN(then)) return '—';

  const seconds = Math.max(0, Math.floor((now - then) / 1000));
  if (seconds < 60) return 'just now';

  const steps: [number, string][] = [
    [60, 'minute'],
    [60, 'hour'],
    [24, 'day'],
  ];
  let amount = seconds;
  let unit = 'second';
  for (const [size, next] of steps) {
    if (amount < size) break;
    amount = Math.floor(amount / size);
    unit = next;
  }
  return `${amount} ${unit}${amount === 1 ? '' : 's'} ago`;
}

/**
 * The class every figure carries, so columns of numbers line up on the decimal
 * point. Exported as a constant rather than left to each caller, because a
 * single table cell that forgets it is visible as a wobble down the column.
 */
export const TABULAR_NUMERALS = 'tabular-nums';
